import LoadingPlaceHolder from '@/components/elements/LoadingPlaceholder';
import { useBookingFlowChrome } from '@/features/booking-flow/BookingFlowChromeContext';
import BookingFlowPage from '@/features/booking-flow/BookingFlowPage';
import { BOOKING_FLOW_STEP_SLUGS } from '@/features/booking-flow/constants';
import { deriveStepSelection } from '@/features/booking-flow/deriveStepSelection';
import { useCalendarOffersEmployeePick } from '@/features/booking-flow/useCalendarOffersEmployeePick';
import { appendPickedService } from '@/features/booking-flow/appendPickedService';
import { buildTargetLocation, goToStep } from '@/features/booking-flow/goToStep';
import { removePickedService } from '@/features/booking-flow/removePickedService';
import { getNextStep, getStepGuardTarget } from '@/features/booking-flow/steps';
import {
  addServiceTrackingHandlers,
  serviceDetailsTrackingHandlers,
  trackEmployeeSelected,
  trackMainServiceAdded,
} from '@/features/booking-flow/tracking';
import { useBookingFlowSelection } from '@/features/booking-flow/useBookingFlowSelection';
import { readPickedIds } from '@/features/booking-flow/urlState';
import withBookingFlowRedesignFlag from '@/features/booking-flow/withBookingFlowRedesignFlag';
import SelectionDrawer from '@/features/booking-selection/components/SelectionDrawer';
import { useSelectionDrawerEmployee } from '@/features/booking-selection/components/SelectionDrawer.hooks';
import SelectionsBar from '@/features/booking-selection/components/SelectionsBar';
import { useSelectionsBar } from '@/features/booking-selection/components/SelectionsBar.hooks';
import { ANY_EMPLOYEE_ID } from '@/features/booking-selection/constants';
import { buildBookSelectionSummary } from '@/features/booking-selection/selectionSummary.helpers';
import { useSelectAddOns } from '@/features/booking-selection/views/SelectAddOns.hooks';
import SelectEmployeeList from '@/features/booking-selection/views/SelectEmployeeList';
import { getBookableEmployeeListItems } from '@/features/booking-selection/views/SelectEmployeeList.helpers';
import { useSelectService } from '@/features/booking-selection/views/SelectService.hooks';
import { useAppSelector } from '@/hooks';
import { _t } from '@/locale';
import { BookState } from '@/types/state/book';
import { useState } from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';

/** The `/valj-utovare` step page (U9b). No `sideColumn` (KTD8). */
const SelectEmployeePage = () => {
  const { placeSlugId } = useParams<{ placeSlugId: string }>();
  const history = useHistory();
  const location = useLocation();
  const book = useAppSelector((state) => state.book) as BookState;
  const { user } = useAppSelector((state) => state.users);
  const place = book.place;
  useBookingFlowSelection(place);
  const calendarOffersEmployeePick = useCalendarOffersEmployeePick();
  const { attribution } = useBookingFlowChrome();
  // Deliberately local, not in the URL: the drawer sits *over* this step rather than being
  // one, so it must not be deep-linkable or restorable.
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { addOnIds: pickedAddOnIds, mainServiceIds: pickedMainServiceIds } = readPickedIds(location.search);

  // Declared above the guards so `useSelectionsBar` runs unconditionally, as on /tillval.
  // Needs no employee lookup: the any-employee `sr` is always the '0' sentinel.
  const onPickAnyEmployee = () => {
    if (!place) return;
    trackEmployeeSelected('any');
    goToStep(
      history,
      placeSlugId,
      getNextStep(BOOKING_FLOW_STEP_SLUGS.SELECT_EMPLOYEE, {
        ...deriveStepSelection(place, location.search, calendarOffersEmployeePick, user),
        employee: ANY_EMPLOYEE_ID,
      }),
      { sr: String(ANY_EMPLOYEE_ID) },
    );
  };

  // The drawer's edits all stay on this step: the visitor came here to pick an employee, so
  // changing the selection must not advance the flow past them. Both drawer lists are the
  // add-service funnel, so either pick is its success leg.
  const appendFromDrawer = (field: 'ss' | 'rs', serviceId: number) =>
    appendPickedService(history, placeSlugId, BOOKING_FLOW_STEP_SLUGS.SELECT_EMPLOYEE, field, serviceId, {
      hydratedServices: book.services,
      track: trackMainServiceAdded,
    });
  const onPickAddOn = (serviceId: number) => appendFromDrawer('rs', serviceId);
  const onAddMainService = (serviceId: number) => appendFromDrawer('ss', serviceId);
  const onRemove = (serviceId: number) =>
    removePickedService(history, placeSlugId, BOOKING_FLOW_STEP_SLUGS.SELECT_EMPLOYEE, serviceId, {
      hydratedServices: book.services,
      pickedAddOnIds,
      pickedMainServiceIds,
    });

  // Hooks run unconditionally (before the guards below can return); all four tolerate an
  // absent place and empty selection.
  const barProps = useSelectionsBar('SelectEmployee', { onPickAnyEmployee, onEdit: () => setIsDrawerOpen(true) });
  const recommendedProps = useSelectAddOns({
    onPickService: onPickAddOn,
    ...serviceDetailsTrackingHandlers,
  });
  const addServiceProps = useSelectService({
    onPickService: onAddMainService,
    ...serviceDetailsTrackingHandlers,
  });
  const { employees: drawerEmployees, selectedEmployeeId: drawerSelectedEmployeeId } = useSelectionDrawerEmployee();

  if (!place) {
    return (
      <BookingFlowPage title="">
        <LoadingPlaceHolder />
      </BookingFlowPage>
    );
  }

  const selection = deriveStepSelection(place, location.search, calendarOffersEmployeePick, user);

  const guardTarget = getStepGuardTarget(BOOKING_FLOW_STEP_SLUGS.SELECT_EMPLOYEE, selection);
  if (guardTarget) {
    return <Redirect to={buildTargetLocation(placeSlugId, guardTarget, location.search)} />;
  }

  const { bookableEmployees, items: employees } = getBookableEmployeeListItems(
    place,
    (selection.services ?? []).map((service) => service.id),
  );

  const onPick = (employeeId: number) => {
    if (employeeId === ANY_EMPLOYEE_ID) {
      onPickAnyEmployee();
      return;
    }

    // sr carries about.externalId, never the internal id — the internal id would make
    // this page's own guard bounce the visitor straight back.
    const sr = bookableEmployees.find((employee) => employee.id === employeeId)?.about?.externalId;
    if (!sr) return;

    trackEmployeeSelected('specific');
    goToStep(
      history,
      placeSlugId,
      getNextStep(BOOKING_FLOW_STEP_SLUGS.SELECT_EMPLOYEE, { ...selection, employee: employeeId }),
      { sr },
    );
  };

  // Drawer rows read hydrated state.book — the display layer, shared with SelectionsBar.
  const pickedServices = book.services ?? [];
  const { items: summaryItems, total } = buildBookSelectionSummary(book, place);

  // "Lägg till tjänst" offers only main services: resolveMainServicesFromUrl drops add-on
  // ids from `ss`, so an add-on row here would be a silent no-op pick.
  const addOnServiceIds = new Set(
    (place.services ?? [])
      .flatMap((category) => category.services ?? [])
      .filter((service) => service.about?.settings?.isAddOn)
      .map((service) => service.id),
  );
  const drawerAddService = {
    ...addServiceProps,
    categories: addServiceProps.categories
      .map((category) => ({
        ...category,
        services: category.services.filter((service) => !addOnServiceIds.has(service.id)),
      }))
      .filter((category) => category.services.length > 0),
  };

  return (
    <BookingFlowPage
      title={_t('features.bookingSelection.SelectEmployeeList.title')}
      subtitle={place.about?.name}
      merchantLogoUrl={place.about?.profileImage}
      bottomBar={<SelectionsBar {...barProps} />}
      emitStepShown>
      <SelectEmployeeList employees={employees} selectedEmployeeId={selection.employee ?? undefined} onPick={onPick} />
      {attribution ? <div className="mt-sm">{attribution('column')}</div> : null}
      {/* Mobile-only by reachability, not CSS: BottomSheetModal portals to document.body, so
          only the SelectionsBar edit button (itself `lg:hidden`) can ever open it. */}
      <SelectionDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        // No `employee` row: this step's guard bounces a visitor who already has one picked,
        // so there is never a pick to show — and with the row gone the employee view is
        // unreachable, the page itself being the list.
        // No `onSearchTimes`: the next step needs the employee this page is still asking for,
        // so the drawer's CTA is this step's own any-employee action instead.
        summary={{ items: summaryItems, total, onRemove }}
        onPickAnyEmployee={onPickAnyEmployee}
        recommended={recommendedProps}
        {...addServiceTrackingHandlers}
        // The modal's rule (SummaryView's `showAddAdditionalService: !pickedAddonServices.length`):
        // once an add-on is picked, adding another main service is no longer offered.
        addService={pickedServices.some((service) => service?.about?.settings?.isAddOn) ? undefined : drawerAddService}
        employeeList={{ employees: drawerEmployees, selectedEmployeeId: drawerSelectedEmployeeId, onPick }}
      />
    </BookingFlowPage>
  );
};

export default withBookingFlowRedesignFlag(SelectEmployeePage);
